import {css, ThemeContext} from '@emotion/react';
import styled from '@emotion/styled';
import color from 'color';
import {percentageFormatter} from '@dnhb/formatters';
import {useMediaQuery} from '@dnhb/node_package';
import { useContext, useEffect, useState } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import ReactTable from 'react-table';
import withFixedColumns from 'react-table-hoc-fixed-columns';
import 'react-table/react-table.css';
import 'react-table-hoc-fixed-columns/lib/styles.css'; // important: this line must be placed after react-table css import
import {PreviousStepButton, RoundedButton} from 'shared/buttons';
import {LabelText, Loader} from 'shared/components';
import {ResultCard} from 'shared/containers';
import {ConversieButtons} from 'shared/conversie';
import {HeaderForm} from 'shared/forms';
import {getParamsFromState, triggerScroll} from 'shared/helpers';
import {SelectInput} from 'shared/inputs';
import analytics from 'shared/utility/analytics';
import {dispatchWithRequest, sync} from 'shared/wizard/actions';
import {whiteListUrlParams} from '../config';

const ReactTableFixedColumns = withFixedColumns(ReactTable);

const LabelDisplay = styled.div`
  min-width: 160px;
  img {
    height: 40px;
  }
  h4 {
    margin-top: 8px;
    font-size: 16px;
    line-height: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.primaryFontColor};
  }

  @media ${({ theme }) => theme.tablet} {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 16px;

    h4 {
      display: none;
    }
  }
`;

const FormContainer = styled.div`
  > button {
    margin-bottom: 24px;
  }

  > div {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    align-items: center;

    ${LabelDisplay} {
      margin-right: 32px;
    }
  }

  @media ${({ theme }) => theme.tablet} {
    > button,
    ${LabelDisplay} {
      display: none;
    }
    > div {
      flex-wrap: wrap;
    }
  }
`;

const SelectWrapper = styled.div`
  flex: 33% 0 1;
  margin-right: 24px;

  @media ${({ theme }) => theme.tablet} {
    flex-basis: 100%;
    margin: 0 0 16px;
  }
`;

const InterestSheetLegend = styled.ul`
    margin-top: 24px;
    margin-left: 24px;
`;

const InterestTable = styled(ReactTableFixedColumns)(
  ({ theme }) => css`
    position: relative;
    margin: 24px 0;
    border: none;
    z-index: 0;

    font-family: ${theme.fontFamily};
    font-size: 14px;
    line-height: 18px;
    color: ${theme.primaryFontColor};
    font-weight: 600;
    letter-spacing: -0.5px;

    border: 1px solid ${theme.borderColor};
    border-radius: 10px;
    background-color: ${theme.cardColor};
    overflow: hidden;

    .rt-thead.-header {
      box-shadow: none;
      margin: 0px;
      z-index: 0;

      .rt-th {
        display: flex;
        justify-content: center;
        border: none;
        outline: none;
        padding: 16px 0;
        overflow: visible;
        background-color: ${theme.cardColor};
        &.rthfc-th-fixed {
          border-right: 1px solid ${theme.borderColor};
          max-width: 120px;
          @media ${theme.mobile} {
            &:first-of-type {
              max-width: 88px;
            }
          }
        }
      }
    }

    .rt-tbody {
      padding: 0;
      .rt-tr-group {
        border-bottom: none;

        &:hover .rt-td {
          font-weight: 800;
          color: ${theme.primaryColor};
          background-color: ${color(theme.primaryColor).alpha(0.15).rgb().string()};
        }
        .rt-td {
          padding: 16px 0;
          border: none;
          text-align: center;
          background-color: ${theme.cardColor};
          border-bottom: 1px dashed ${theme.borderColor};

          &.row-header {
            background-color: ${theme.cardColor};
            padding: 16px;
            color: ${theme.primaryColor};
            border-bottom: none;
            border-right: 1px solid ${theme.borderColor};
            text-align: right;
            max-width: 120px;

            @media ${theme.mobile} {
              max-width: 88px;
            }
          }

          &.active {
            color: ${theme.primaryColor};
            font-weight: 800;
          }
        }

        &:last-of-type .rt-td {
          border-bottom: none;
        }
      }
    }
  `
);

const InterestSheet = () => {
  const { app, framed, input, output, settings } = useSelector((state) => state);
  const dispatch = useDispatch();
  const syncAction = sync(dispatch);
  const base = `/${app}${framed ? '/framed' : ''}`;
  const { errors, interestSheet = {} } = output;
  const [mobileFormOpen, setMobileFormOpen] = useState(true);
  const [activeColumn, setActiveColumn] = useState(null);
  const { labelId: paramLabelId } = useParams();
  const [labelId, setLabelId] = useState(paramLabelId);
  const [mortgageTypeId, setMortgageTypeId] = useState(null);
  const { tablet } = useContext(ThemeContext);
  const navigate = useNavigate();
  const tabletView = useMediaQuery(tablet || '(max-width: 963px)');

  useEffect(() => {
    analytics.send('Openen', `interest-sheet/${labelId}`);

    if (paramLabelId !== labelId) {
      navigate(`${base}/interest-sheet/${labelId}`, { replace: true });
    }

    dispatch(
      dispatchWithRequest('label_id', labelId, '/renteoverzicht/products-by-label', 'labelProducts', (data) => {
        const product = data.find((product) => {
            return product.mortgage_type === input.mortgage_type
        });

        setMortgageTypeId(product?.product_id ?? data[0]);

        dispatch(
          dispatchWithRequest('product_id', product?.product_id ?? data[0], '/renteoverzicht/calculate/interest-sheet', 'interestSheet')
        );
      })
    );
  }, [base, dispatch, navigate, labelId, paramLabelId]);

  const BackButton = () => (
    <PreviousStepButton
      handleClick={() => {
        navigate(`${base}/all`);
        triggerScroll();
      }}
    >
      Terug naar alle aanbieders
    </PreviousStepButton>
  );

  const { products = [], labels = []} = interestSheet.meta || {};

  const mortgageTypesOfLabel = products.map((p) => ({
    label: p.product_name,
    value: p.product_id,
    type: p.mortgage_type,
  }));

  if (!interestSheet || Object.keys(interestSheet).length === 0) {
    return (
      <ResultCard>
        <BackButton />
        <Loader />
      </ResultCard>
    );
  }

  const {logo_url, mortgage_provider_name, label_id, interest_rates, continuation_interest_rates} = interestSheet.data;
  const interestRatesForNew = Object.values(interest_rates);
  const continuationInterestRates = Object.values(continuation_interest_rates);
  const columnKeys = Object.keys(continuationInterestRates[0]).filter((key) => !['frt', 'isVariable'].includes(key));
  const columnMedian = columnKeys[Math.round(columnKeys.length / 2)];
  const mortgageTypeValue = mortgageTypesOfLabel.find((type) => type.type === input.mortgage_type);
  const mortgageTypeLabel = mortgageTypeValue?.label
  const providerLabel = (labels.find((label) => label.value === label_id) || {}).label;
  const tableColumns = [
    {
      Header: '',
      accessor: 'frt',
      className: 'row-header',
      fixed: 'left',
      Cell: ({ original: { isVariable }, value }) => {
        if (isVariable) return 'Variabel';
        return `${value / 12} jaar`;
      },
    },
  ];
  columnKeys.map((columnKey) => {
      // When it is 0% NHG it does not add the number to the string
      const hasNumber = /\d/;
      const columnLabel = columnKey.includes('NHG') && !hasNumber.test(columnKey) ? 'NHG' : columnKey;
      return tableColumns.push({
          Header: ({column}) => (
              <RoundedButton
                  active={activeColumn ? column.id === activeColumn : column.id === columnMedian}
                  onClick={() => setActiveColumn(columnKey)}
              >
                  {columnLabel.replace('<=', '≤')}
              </RoundedButton>
          ),
          accessor: columnKey,
          minWidth: 92,
          Cell: ({original}) => {
              // Find interestRatesForNew by rateFixation
              const rowForNew = interestRatesForNew.find((row) => row.frt === original.frt && row.isVariable === original.isVariable);

              if (rowForNew === undefined || !rowForNew[columnKey]) {
                  return original[columnKey] ? `${percentageFormatter(original[columnKey])} *` : '-'
              }

              return (rowForNew[columnKey] ? percentageFormatter(rowForNew[columnKey]) : '-')
          },
          getProps: (state, row, column) => {
              const isActive = activeColumn ? column.id === activeColumn : column.id === columnMedian;
              return {className: isActive && 'active'};
          },
      })
  });

  return (
    <>
      <HeaderForm
        buttonTitle={mobileFormOpen ? 'Aanpassen' : 'Toon renteblad'}
        setMobileFormOpen={() => setMobileFormOpen(!mobileFormOpen)}
        mobileFormOpen={mobileFormOpen}
        mobilePrefix={
          <>
            <BackButton />
            <LabelDisplay>
              <img
                  src={logo_url}
                  alt={mortgage_provider_name}
              />
            </LabelDisplay>
          </>
        }
        tags={[
          {
            label: 'Label',
            value: providerLabel,
          },
          {
            label: 'Aflosvorm',
            value: mortgageTypeLabel,
          },
        ]}
        hasErrors={errors === true}
      >
        <FormContainer>
          <BackButton />
          <div>
            <LabelDisplay>
              <img
                  src={logo_url}
                  alt={mortgage_provider_name}
              />
            </LabelDisplay>
            {labels.length > 1 && (
              <SelectWrapper>
                <LabelText>Label van verstrekker</LabelText>
                <SelectInput
                  value={Number(labelId) || label_id}
                  options={labels}
                  onChange={(change) => setLabelId(change.value)}
                  noOptionsMessage={() => 'Geen resultaten'}
                  placeholder="Labels"
                />
              </SelectWrapper>
            )}
            <SelectWrapper>
              <LabelText>Aflosvorm</LabelText>
              <SelectInput
                name="mortgage_type_id"
                value={mortgageTypeId ?? mortgageTypeValue.value}
                options={mortgageTypesOfLabel}
                onChange={(change) => {
                  sync('mortgage_type', change.type);
                  setMortgageTypeId(change.value);

                  dispatch(
                    dispatchWithRequest(
                      'product_id',
                      change.value,
                      '/renteoverzicht/calculate/interest-sheet',
                      'interestSheet'
                    )
                  );
                }}
                noOptionsMessage={() => 'Geen resultaten'}
                placeholder="Aflosvorm"
              />
            </SelectWrapper>
          </div>
        </FormContainer>
      </HeaderForm>
      {(!tabletView || mobileFormOpen) && (
        <>
         <InterestSheetLegend>
             <li>* Alleen beschikbaar voor continuatie (niet beschikbaar voor nieuwe hypotheken)</li>
         </InterestSheetLegend>
          <InterestTable
            loading={!interest_rates}
            data={continuationInterestRates}
            columns={tableColumns}
            minRows={1}
            defaultPageSize={31}
            showPageSizeOptions={false}
            showPagination={false}
            sortable={false}
            resizable={false}
          />
          <ConversieButtons
            app={app}
            settings={settings}
            urlLeadFormParams={getParamsFromState(
              { ...input, ...output, providerLabel, mortgageTypeLabel },
              whiteListUrlParams
            )}
            onClick={() => syncAction('label_id', labelId)}
          />
        </>
      )}
    </>
  );
};

export default InterestSheet;
